import { FC, PropsWithChildren, ReactElement } from 'react';

import { DefaultHelmet } from '../../../../../compositions';
import { ProductAttributeKey, ProductDimensionAttributeKey } from '../../../../../entities/@api/Sylius';
import { ProductSeoAvailability, ProductSeoCondition } from '../../../../../entities/@products/Product/Product';
import { ProductDefault } from '../../../../../entities/@products/ProductDefault/ProductDefault';
import { ProductVariant } from '../../../../../entities/@products/ProductVariant/ProductVariant';
import { Image } from '../../../../../entities/Media/Media';
import { Currency } from '../../../../../entities/Price/Price';
import { AppRoute, appRoutes } from '../../../../../entities/Routing/Routing';
import { replaceUrlParamKeysWithValues } from '../../../../../helpers/url';
import { useTrans } from '../../../../../hooks';

interface ProductDetailHelmetProps extends PropsWithChildren {
    productDefault: ProductDefault;
    productVariant: ProductVariant;
}

export const ProductDetailHelmet: FC<ProductDetailHelmetProps> = ({
    productDefault,
    productVariant,
    children,
}): ReactElement => {
    const trans = useTrans();

    const siteUrl = trans('company.url');

    const fallbackImage = {
        src: `${siteUrl}/images/metadata-preview.jpg`,
        alt: trans('company.name'),
    };

    const metaTitle = productVariant.name;
    const metaDescription = productDefault.shortDescription || metaTitle;

    const productImages = productDefault.mediaItems
        .filter(mediaItem => !!mediaItem.image)
        .map(mediaItem => mediaItem.image as Image);

    const metaImage = productImages.length > 0
        ? productImages[0]
        : fallbackImage;

    const productPathTemplate = trans(appRoutes[AppRoute.productDetail].path);
    const productPath = replaceUrlParamKeysWithValues(productPathTemplate, {
        slug: productVariant.relativeUrl,
    });

    const productUrl = siteUrl + productPath;

    const structuredImages = productImages.map(image => image.src);

    const structuredBrand = productVariant.brand
        ? {
            '@type': 'Brand',
            name: productVariant.brand.label,
        }
        : undefined;

    // TODO: ProductSeoAvailability.inStoreOnly check
    // TODO: Implement getProductStructuredDataAvailability
    const structuredAvailability = productVariant.stockQuantity > 0
        ? ProductSeoAvailability.inStock
        : ProductSeoAvailability.outOfStock;

    const structuredSku = productVariant.attributes.find(attribute => attribute.key === ProductAttributeKey.sku);
    const structuredMaterial = productVariant.attributes.find(attribute => attribute.key === ProductAttributeKey.materials);
    const structuredColor = productVariant.attributes.find(attribute => attribute.key === ProductAttributeKey.colors);

    const structuredWidth = productVariant.dimensionAttributes.find(attribute => attribute.key === ProductDimensionAttributeKey.width);
    const structuredHeight = productVariant.dimensionAttributes.find(attribute => attribute.key === ProductDimensionAttributeKey.height);
    const structuredDepth = productVariant.dimensionAttributes.find(attribute => attribute.key === ProductDimensionAttributeKey.depth);
    const structuredWeight = productVariant.dimensionAttributes.find(attribute => attribute.key === ProductDimensionAttributeKey.weight);

    return (
        <DefaultHelmet
            title={metaTitle}
            description={metaDescription}
            image={metaImage}
            canonical={productUrl}
        >
            <script type="application/ld+json">
                {JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'Product',
                    name: productVariant.name,
                    description: productVariant.description,
                    image: structuredImages,
                    brand: structuredBrand,
                    sku: structuredSku?.value,
                    offers: {
                        '@type': 'Offer',
                        url: productUrl,
                        priceCurrency: Currency.eur,
                        price: productVariant.price.current,
                        availability: structuredAvailability,
                        itemCondition: ProductSeoCondition.new,
                        seller: {
                            '@type': 'Organization',
                            name: trans('company.name'),
                        },
                    },
                    category: productDefault.categories?.at(-1),
                    material: structuredMaterial?.value,
                    color: structuredColor?.value,
                    weight: structuredWeight?.value,
                    width: structuredWidth?.value,
                    height: structuredHeight?.value,
                    depth: structuredDepth?.value,
                })}
            </script>

            {children}
        </DefaultHelmet>
    );
};
